import {
  type GridView,
  useFetchGridViews,
  useRetainedState
} from "@appretio/shared";
import { useCallback, useMemo, useState } from "react";
import { type CubePage, pageScope } from "../hooks/useCubeSelectionState";
import { useOpenOnce } from "../hooks/useOpenOnce";
import { type ActiveView, useGridViewWrites } from "./useGridViewWrites";

/** ``act``, spending the list's opening first. */
function spendingOpen<A extends unknown[], R>(
  markOpened: () => void,
  act: (...args: A) => R
): (...args: A) => R {
  return (...args) => {
    markOpened();
    return act(...args);
  };
}

/**
 * ``save``, spending the list's opening once it lands. A rejected or
 * abandoned save spends nothing, so the opening still runs once the save
 * dialog closes.
 */
function spendingOpenOnSave<A extends unknown[], R>(
  markOpened: () => void,
  save: (...args: A) => Promise<R>
): (...args: A) => Promise<R> {
  return async (...args) => {
    const saved = await save(...args);
    if (saved) markOpened();
    return saved;
  };
}

/**
 * The list's opening (``useOpenOnce``), held while the save dialog is open:
 * it would move the state the save captures. The dialog cannot close while a
 * save is in flight, so the hold covers the request too. The picker reports
 * the dialog through ``setSaveOpen``.
 */
function useHeldOpening(
  scope: string | undefined,
  ready: boolean,
  open: () => void
) {
  const [saveOpen, setSaveOpen] = useState(false);
  const markOpened = useOpenOnce(scope, "view", ready && !saveOpen, open);
  return { markOpened, setSaveOpen };
}

/** What a page's Views layer needs to run one saved-view list. */
export interface UseSavedViewListParams<P, K extends string, S> {
  /** The page whose list this is: its ``GridKey`` and its retained scope. */
  page: CubePage;
  /**
   * Keeps the applied view and the opening to this mount instead of the
   * page's scope: a grid inside another surface (the item modal) runs the
   * page's list without touching the page's applied view.
   */
  transient?: boolean;
  /**
   * The built-in every opening lands, in place of the user's default — for
   * a grid inside another surface that opens on a setup of its own.
   */
  openOn?: K;
  /** The code-owned views, in picker order. */
  builtIns: Record<K, P>;
  /** The view the page opens on with no default of the user's own. */
  defaultBuiltIn: K;
  /** Reads a stored ``control_state``; null when this build can't replay it. */
  parse: (controlState: string | null | undefined) => P | null;
  /** Lands a payload on the page. */
  applyPayload: (payload: P) => void;
  /**
   * What the opening waits for — the metadata, say. A default opening also
   * waits for the list; an ``openOn`` opening does not.
   */
  readyToOpen: boolean;
  /** Snapshots the toggled sections of the live state. */
  capture: (sections: S) => string;
  /** Which sections a stored payload carries. */
  sectionsOf: (payload: P) => S;
  /** Every section on, for refreshing a payload that can't be read. */
  allSections: S;
}

/**
 * One saved-view list as a page runs it: the fetch, which view is applied
 * (retained per page, so coming back finds it), the one apply path for
 * built-ins and saved views, the opening on a fresh session, and the writes
 * behind the picker's housekeeping. What a view *sets* is the caller's:
 * ``applyPayload`` and ``capture`` are the only page-specific parts.
 */
export function useSavedViewList<
  P extends { version: number },
  K extends string,
  S
>({
  page,
  transient = false,
  openOn,
  builtIns,
  defaultBuiltIn,
  parse,
  applyPayload,
  readyToOpen,
  capture,
  sectionsOf,
  allSections
}: UseSavedViewListParams<P, K, S>) {
  const { data: savedViews, isLoading } = useFetchGridViews(page);
  const views = useMemo(() => savedViews ?? [], [savedViews]);
  const scope = transient ? undefined : pageScope(page);
  const [activeView, setActiveView] = useRetainedState<ActiveView<K>>(
    scope,
    "activeView",
    { kind: "none" }
  );

  const activeSavedView =
    activeView.kind === "saved"
      ? views.find((view) => view.view_id === activeView.viewId)
      : undefined;
  const activePayload = useMemo((): P | null => {
    if (activeView.kind === "builtIn") return builtIns[activeView.key];
    return activeSavedView ? parse(activeSavedView.control_state) : null;
  }, [activeView, activeSavedView, builtIns, parse]);

  const landBuiltIn = useCallback(
    (key: K) => {
      setActiveView({ kind: "builtIn", key });
      applyPayload(builtIns[key]);
    },
    [applyPayload, builtIns, setActiveView]
  );

  const landSavedView = useCallback(
    (view: GridView) => {
      setActiveView({ kind: "saved", viewId: view.view_id });
      const payload = parse(view.control_state);
      if (payload) applyPayload(payload);
    },
    [applyPayload, parse, setActiveView]
  );

  // A fresh session opens the page on a view — the user's default, else the
  // built-in — so the grid never starts unnamed; coming back to the page
  // keeps the view it had. A default opening waits for the views to have
  // *landed*: a failed fetch would otherwise open on the built-in and spend
  // the one shot the user's default had. An ``openOn`` opening ignores the
  // list, so it does not wait for it.
  const openOnDefault = useCallback(() => {
    const defaultView = openOn
      ? undefined
      : views.find((view) => view.is_default);
    if (defaultView) landSavedView(defaultView);
    else landBuiltIn(openOn ?? defaultBuiltIn);
  }, [openOn, views, landSavedView, landBuiltIn, defaultBuiltIn]);
  const { markOpened, setSaveOpen } = useHeldOpening(
    scope,
    (openOn !== undefined || savedViews !== undefined) && readyToOpen,
    openOnDefault
  );

  // A pick or a save names the applied view, so it spends the opening: the
  // picker is live before the list or the caller is ready, and the opening
  // would overwrite it. An edit or a delete acts on an applied saved view,
  // so the opening is spent by then.
  const applyBuiltIn = useMemo(
    () => spendingOpen(markOpened, landBuiltIn),
    [markOpened, landBuiltIn]
  );
  const applySavedView = useMemo(
    () => spendingOpen(markOpened, landSavedView),
    [markOpened, landSavedView]
  );

  const forRefresh = useCallback(
    (view: GridView) => {
      const stored = parse(view.control_state);
      return capture(stored ? sectionsOf(stored) : allSections);
    },
    [parse, capture, sectionsOf, allSections]
  );
  const writes = useGridViewWrites(
    page,
    { forSave: capture, forRefresh },
    activeSavedView,
    setActiveView
  );
  const { saveCurrentView } = writes;
  const saveSpendingOpen = useMemo(
    () => spendingOpenOnSave(markOpened, saveCurrentView),
    [markOpened, saveCurrentView]
  );

  return {
    ...writes,
    saveCurrentView: saveSpendingOpen,
    setSaveOpen,
    views,
    isLoading,
    activeView,
    activeSavedView,
    activePayload,
    applyBuiltIn,
    applySavedView,
    markOpened
  };
}
